import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Coins, Flame, Trophy, Brain, ChevronRight, LogOut, 
  Star, Calendar, Award, Sparkles, Settings, Lock, Ticket, Loader2, Crown
} from 'lucide-react';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import MilestoneAnimation from '../components/MilestoneAnimation';
import MilitaryRankBadge from '../components/MilitaryRankBadge';
import NotificationManager from '../components/NotificationManager';

const AVATAR_EMOJIS = {
  Lion: '🦁',
  Cat: '🐱',
  Dog: '🐶',
  Panda: '🐼',
  Rabbit: '🐰',
  Owl: '🦉',
};

const DashboardPage = () => {
  const { user, api, logout, refreshUser } = useAuth();
  const [progress, setProgress] = useState(null);
  const [currentSession, setCurrentSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMilestone, setShowMilestone] = useState(null);
  const [startingSession, setStartingSession] = useState(false);
  const [militaryRank, setMilitaryRank] = useState(null);

  // Mystery Box surprise popup
  const [showMysteryBox, setShowMysteryBox] = useState(false);
  const [mysteryReward, setMysteryReward] = useState(null);
  const [boxOpening, setBoxOpening] = useState(false);
  const [boxOpened, setBoxOpened] = useState(false);
  const navigate = useNavigate();

  // Boss popup state
  const [showBoss, setShowBoss] = useState(false);
  const [bossQuestion, setBossQuestion] = useState(null);
  const [bossAnswer, setBossAnswer] = useState(null);
  const [bossResult, setBossResult] = useState(null);
  const [bossLoading, setBossLoading] = useState(false);
  const [bossTimer, setBossTimer] = useState(0);
  const [bossTimerRunning, setBossTimerRunning] = useState(false);
  const [bossFailMessages, setBossFailMessages] = useState([]);
  const [bossWinMessages, setBossWinMessages] = useState([]);
  const bossTimerRef = useRef(null);
  const bossAudioCtxRef = useRef(null);

  // Renewal state
  const [showRenewModal, setShowRenewModal] = useState(false);
  const [renewCode, setRenewCode] = useState('');
  const [renewLoading, setRenewLoading] = useState(false);
  const [renewTab, setRenewTab] = useState('options'); // options | code
  const [trialLoading, setTrialLoading] = useState(false);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [subStatus, setSubStatus] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);

  const isExpired = user?.access_expired === true;
  const trialUsed = user?.trial_used === true;
  const expiresAt = user?.access_expires_at;
  const daysLeft = expiresAt ? Math.max(0, Math.ceil((new Date(expiresAt) - new Date()) / (1000 * 60 * 60 * 24))) : null;

  useEffect(() => {
    fetchData();
    checkMilestones();
    checkMysteryBox();
    checkBoss();
    fetchSubStatus();
  }, []);

  const checkMysteryBox = async () => {
    try {
      const res = await api.get('/mystery-box/check');
      if (res.data.available) {
        const delay = 8000 + Math.random() * 7000;
        setTimeout(() => setShowMysteryBox(true), delay);
      }
    } catch (e) { /* */ }
  };

  const checkBoss = async () => {
    try {
      const res = await api.get('/boss/check');
      if (!res.data.available) return;
      
      const completedAt = res.data.completed_at;
      if (!completedAt) return;
      
      const elapsed = Date.now() - new Date(completedAt).getTime();
      const minDelay = 3 * 60 * 1000;
      const maxDelay = 4 * 60 * 1000;
      const targetDelay = minDelay + Math.random() * (maxDelay - minDelay);
      const remaining = Math.max(0, targetDelay - elapsed);
      
      setTimeout(async () => {
        try {
          const qRes = await api.get('/boss/question');
          if (qRes.data.boss_question) {
            setBossQuestion(qRes.data.boss_question);
            setBossFailMessages(qRes.data.fail_messages || []);
            setBossWinMessages(qRes.data.win_messages || []);
            setBossTimer(qRes.data.boss_question.timer || 5);
            setShowBoss(true);
            setBossTimerRunning(true);
            playBossAppearSound();
          }
        } catch (e) { /* */ }
      }, remaining);
    } catch (e) { /* */ }
  };

  // Boss countdown timer
  useEffect(() => {
    if (!bossTimerRunning || bossResult || bossTimer <= 0) return;
    
    // Tick sound on each second
    if (bossTimer <= 3) playBossTickSound();
    
    bossTimerRef.current = setTimeout(() => {
      setBossTimer(prev => {
        if (prev <= 1) {
          // TIME'S UP — auto-submit as timeout
          setBossTimerRunning(false);
          handleBossTimeout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearTimeout(bossTimerRef.current);
  }, [bossTimer, bossTimerRunning, bossResult]);

  // === Boss Sound Effects ===
  const getAudioCtx = () => {
    if (!bossAudioCtxRef.current) {
      bossAudioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    return bossAudioCtxRef.current;
  };

  const playBossAppearSound = () => {
    try {
      const ctx = getAudioCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(200, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(600, ctx.currentTime + 0.3);
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.5);
      const osc2 = ctx.createOscillator();
      const gain2 = ctx.createGain();
      osc2.connect(gain2);
      gain2.connect(ctx.destination);
      osc2.type = 'triangle';
      osc2.frequency.setValueAtTime(400, ctx.currentTime + 0.25);
      osc2.frequency.exponentialRampToValueAtTime(800, ctx.currentTime + 0.55);
      gain2.gain.setValueAtTime(0.25, ctx.currentTime + 0.25);
      gain2.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.7);
      osc2.start(ctx.currentTime + 0.25);
      osc2.stop(ctx.currentTime + 0.7);
    } catch { /* */ }
  };

  const playBossTickSound = () => {
    try {
      const ctx = getAudioCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.08);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.08);
    } catch { /* */ }
  };

  const playBossVictorySound = () => {
    try {
      const ctx = getAudioCtx();
      const notes = [523, 659, 784, 1047];
      notes.forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'square';
        osc.frequency.setValueAtTime(freq, ctx.currentTime + i * 0.15);
        gain.gain.setValueAtTime(0.2, ctx.currentTime + i * 0.15);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + i * 0.15 + 0.3);
        osc.start(ctx.currentTime + i * 0.15);
        osc.stop(ctx.currentTime + i * 0.15 + 0.3);
      });
    } catch { /* */ }
  };

  const playBossFailSound = () => {
    try {
      const ctx = getAudioCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(300, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(100, ctx.currentTime + 0.4);
      gain.gain.setValueAtTime(0.2, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.5);
    } catch { /* */ }
  };

  const handleBossTimeout = async () => {
    if (!bossQuestion) return;
    setBossLoading(true);
    try {
      const res = await api.post('/boss/answer', {
        answer: '',
        correct_answer: bossQuestion.correct_answer,
        question: bossQuestion.question,
        timed_out: true,
      });
      setBossResult({ ...res.data, timed_out: true });
      playBossFailSound();
    } catch {
      setBossResult({ correct: false, coins: 0, timed_out: true });
      playBossFailSound();
    }
    setBossLoading(false);
  };

  const handleBossSubmit = async () => {
    if (!bossQuestion || !bossAnswer || bossLoading) return;
    setBossTimerRunning(false);
    clearTimeout(bossTimerRef.current);
    setBossLoading(true);
    try {
      const res = await api.post('/boss/answer', {
        answer: bossAnswer,
        correct_answer: bossQuestion.correct_answer,
        question: bossQuestion.question,
        timed_out: false,
      });
      setBossResult(res.data);
      if (res.data.correct) {
        playBossVictorySound();
      } else {
        playBossFailSound();
      }
      if (res.data.coins) await refreshUser();
    } catch (e) {
      setBossResult({ correct: false, coins: 0 });
      playBossFailSound();
    }
    setBossLoading(false);
  };

  const handleBossDismiss = () => {
    setShowBoss(false);
    setBossQuestion(null);
    setBossAnswer(null);
    setBossResult(null);
    setBossTimerRunning(false);
    setBossTimer(0);
    clearTimeout(bossTimerRef.current);
  };

  const handleOpenMysteryBox = async () => {
    if (boxOpening) return;
    setBoxOpening(true);
    try {
      const res = await api.post('/mystery-box/claim');
      if (res.data.already_claimed) {
        setShowMysteryBox(false);
        return;
      }
      setTimeout(() => {
        setMysteryReward(res.data);
        setBoxOpened(true);
        setBoxOpening(false);
        refreshUser();
      }, 2200);
    } catch (e) {
      setShowMysteryBox(false);
      setBoxOpening(false);
    }
  };

  const handleRenew = async () => {
    if (!renewCode.trim() || renewLoading) return;
    setRenewLoading(true);
    try {
      await api.post('/auth/renew', { enrollment_code: renewCode.trim() });
      toast.success('Access renewed for 12 more weeks!');
      setShowRenewModal(false);
      setRenewCode('');
      setRenewTab('options');
      await refreshUser();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid code. Try again!');
    } finally {
      setRenewLoading(false);
    }
  };

  const handleStartTrial = async () => {
    if (trialLoading) return;
    setTrialLoading(true);
    try {
      await api.post('/auth/start-trial');
      toast.success('7-day free trial started!');
      setShowRenewModal(false);
      await refreshUser();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start trial.');
    } finally {
      setTrialLoading(false);
    }
  };

  const handleStripePayment = async () => {
    if (stripeLoading) return;
    setStripeLoading(true);
    try {
      const origin = window.location.origin;
      const res = await api.post('/payments/create-checkout', { origin_url: origin });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (e) {
      toast.error('Could not start payment. Try again!');
      setStripeLoading(false);
    }
  };

  const fetchSubStatus = async () => {
    try {
      const res = await api.get('/payments/subscription-status');
      setSubStatus(res.data);
    } catch { /* */ }
  };

  const handleCancelSubscription = async () => {
    if (cancelLoading) return;
    if (!window.confirm('Are you sure you want to cancel your subscription? You will keep access until your current period ends.')) return;
    setCancelLoading(true);
    try {
      await api.post('/payments/cancel-subscription');
      toast.success('Subscription cancelled. Access remains until it expires.');
      setSubStatus(prev => prev ? { ...prev, active: false, status: 'canceled' } : prev);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not cancel. Try again.');
    } finally {
      setCancelLoading(false);
    }
  };

  const formatRenewCode = (value) => {
    const cleaned = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    if (cleaned.length <= 5) return cleaned;
    if (cleaned.length <= 9) return `${cleaned.slice(0, 5)}-${cleaned.slice(5)}`;
    return `${cleaned.slice(0, 5)}-${cleaned.slice(5, 9)}-${cleaned.slice(9, 13)}`;
  };

  const fetchData = async () => {
    try {
      const [progressRes, sessionRes, rankRes] = await Promise.all([
        api.get('/progress'),
        api.get('/session/current'),
        api.get('/military-rank').catch(() => ({ data: { rank: null } })),
      ]);
      setProgress(progressRes.data);
      setCurrentSession(sessionRes.data);
      setMilitaryRank(rankRes.data.rank);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkMilestones = () => {
    // Check for milestone celebrations
    const stored = localStorage.getItem('lastMilestones');
    const milestones = stored ? JSON.parse(stored) : {};
    
    // 7-day streak
    if (user?.streak === 7 && !milestones.streak7) {
      setShowMilestone('streak');
      localStorage.setItem('lastMilestones', JSON.stringify({ ...milestones, streak7: true }));
    }
    // Week completion (every 7 sessions)
    else if (user?.total_sessions > 0 && user?.total_sessions % 7 === 0 && milestones.lastWeek !== Math.floor(user?.total_sessions / 7)) {
      setShowMilestone('week');
      localStorage.setItem('lastMilestones', JSON.stringify({ ...milestones, lastWeek: Math.floor(user?.total_sessions / 7) }));
    }
    // Level up
    else if (user?.math_level > 1 && milestones.lastLevel !== user?.math_level) {
      setShowMilestone('levelup');
      localStorage.setItem('lastMilestones', JSON.stringify({ ...milestones, lastLevel: user?.math_level }));
    }
  };

  const handleStartSession = async () => {
    if (startingSession) return;
    setStartingSession(true);
    
    // Navigate immediately to show loading state on SessionPage
    // The session will be loaded there
    navigate('/session');
  };

  const handleLogout = () => {
    // Single-action logout - clear session first, then redirect immediately
    logout();
    // Use window.location for immediate hard redirect to avoid React state timing issues
    window.location.href = '/login';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <img 
            src="/brain-gym-logo.png" 
            alt="Brain Gym Jr. Logo" 
            className="w-32 h-32 mx-auto mb-4 animate-bounce"
          />
          <p className="text-xl text-slate-600">Loading your adventure...</p>
        </div>
      </div>
    );
  }

  const canPlay = currentSession?.can_play !== false;
  const weekProgress = progress?.current_week || 1;
  const journeyProgress = ((progress?.total_sessions || 0) / 84) * 100;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="dashboard-page">
      {/* Milestone Animation */}
      {showMilestone && (
        <MilestoneAnimation type={showMilestone} onComplete={() => setShowMilestone(null)} />
      )}
      
      {/* Header */}
      <header className="bg-white border-b-2 border-slate-100 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center text-2xl">
              {AVATAR_EMOJIS[user?.avatar] || '🧠'}
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-bold text-slate-900">{user?.display_name || user?.name}</p>
                {militaryRank && <MilitaryRankBadge rank={militaryRank} size="small" />}
              </div>
              <p className="text-sm text-slate-500">Age {user?.age}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-1 sm:gap-4 shrink-0">
            <div className="flex items-center gap-1 bg-primary/20 px-2 sm:px-4 py-1.5 sm:py-2 rounded-full" data-testid="coin-counter">
              <Coins className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
              <span className="font-bold text-slate-900 text-xs sm:text-base">{user?.coins || 0}</span>
            </div>
            
            <button
              onClick={() => navigate('/rewards')}
              className="p-1 sm:p-2 hover:bg-slate-100 rounded-full transition-colors"
              data-testid="badges-btn"
              title="Badge Collection"
            >
              <Star className="w-5 h-5 sm:w-6 sm:h-6 text-amber-500" />
            </button>

            <button
              onClick={() => navigate('/account-settings')}
              className="p-1 sm:p-2 hover:bg-slate-100 rounded-full transition-colors"
              data-testid="account-settings-btn"
              title="Account Settings"
            >
              <Settings className="w-5 h-5 sm:w-6 sm:h-6 text-slate-400" />
            </button>
            
            <button
              onClick={handleLogout}
              className="p-1 sm:p-2 hover:bg-slate-100 rounded-full transition-colors"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5 sm:w-6 sm:h-6 text-slate-400" />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Access Expired Banner */}
        {isExpired && (
          <div data-testid="access-expired-banner" className="card-bubbly bg-gradient-to-br from-red-50 to-orange-50 border-2 border-red-200">
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                <Lock className="w-8 h-8 text-red-500" />
              </div>
              <div className="flex-1 text-center sm:text-left">
                <h3 className="text-xl font-black text-slate-900 font-fredoka">Your 12-Week Journey Has Ended!</h3>
                <p className="text-slate-600 mt-1">Enter a new enrollment code to continue your brain training adventure.</p>
              </div>
              <button
                onClick={() => setShowRenewModal(true)}
                className="btn-primary flex items-center gap-2"
                data-testid="renew-access-btn"
              >
                <Ticket className="w-5 h-5" />
                Renew Access
              </button>
            </div>
          </div>
        )}

        {/* Days Left Warning (last 7 days) */}
        {!isExpired && daysLeft !== null && daysLeft <= 7 && (
          <div data-testid="access-expiring-soon" className="card-bubbly bg-gradient-to-br from-amber-50 to-yellow-50 border-2 border-amber-200">
            <div className="flex items-center gap-3">
              <span className="text-2xl">&#9200;</span>
              <p className="text-amber-800 font-bold text-sm">
                {daysLeft === 0 ? 'Your access expires today!' : `Only ${daysLeft} day${daysLeft > 1 ? 's' : ''} left in your 12-week journey!`}
              </p>
              <button onClick={() => setShowRenewModal(true)} className="ml-auto text-sm font-bold text-amber-700 underline" data-testid="renew-early-btn">
                Renew Now
              </button>
            </div>
          </div>
        )}

        {/* Welcome Banner */}
        <div className="card-bubbly bg-gradient-to-br from-primary/10 to-secondary/10">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="flex-1">
              <h1 className="text-3xl md:text-4xl font-black text-slate-900 font-fredoka mb-2">
                {isExpired ? "Your Journey Has Ended!" : canPlay ? "Ready to Train Your Brain?" : "Great Job Today!"}
              </h1>
              <p className="text-lg text-slate-600 mb-4">
                {isExpired
                  ? "Renew your access to continue brain training!"
                  : canPlay 
                    ? "Your 10-minute brain workout awaits!"
                    : "Come back tomorrow for more fun puzzles!"}
              </p>
              
              {isExpired ? (
                <button
                  onClick={() => setShowRenewModal(true)}
                  className="btn-primary inline-flex items-center gap-2"
                  data-testid="start-session-btn"
                >
                  <Ticket className="w-5 h-5" />
                  Renew to Continue
                  <ChevronRight className="w-5 h-5" />
                </button>
              ) : (
                <button
                  onClick={handleStartSession}
                  disabled={!canPlay || startingSession}
                  className={`${canPlay && !startingSession ? 'btn-primary' : 'btn-secondary opacity-50'} inline-flex items-center gap-2`}
                  data-testid="start-session-btn"
                >
                  {startingSession ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Starting...
                    </>
                  ) : canPlay ? (
                    <>
                      <Sparkles className="w-5 h-5" />
                      Start Today's Brain Workout
                      <ChevronRight className="w-5 h-5" />
                    </>
                  ) : (
                    <>
                      <Calendar className="w-5 h-5" />
                      See You Tomorrow!
                    </>
                  )}
                </button>
              )}
            </div>
            
            <div className="text-8xl animate-bounce-soft">
              {isExpired ? '🔒' : canPlay ? '🚀' : '🌟'}
            </div>
          </div>
        </div>

        {/* Daily Riddles Section */}
        <button
          onClick={() => isExpired ? setShowRenewModal(true) : navigate('/riddles')}
          className={`w-full card-bubbly bg-gradient-to-br from-purple-100 via-pink-50 to-amber-50 text-left hover-bounce tap-shrink border-2 border-purple-200 relative ${isExpired ? 'opacity-70' : ''}`}
          data-testid="riddles-btn"
        >
          {isExpired && (
            <div className="absolute top-2 right-2 bg-red-100 rounded-full p-1.5">
              <Lock className="w-4 h-4 text-red-500" />
            </div>
          )}
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-pink-500 rounded-2xl flex items-center justify-center shadow-lg">
              <span className="text-4xl">&#129513;</span>
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-slate-900 font-fredoka">
                Daily Riddles
              </h3>
              <p className="text-slate-600">
                {isExpired ? 'Renew access to solve riddles!' : 'Solve 3 fun riddles and earn Brain Coins!'}
              </p>
            </div>
            {!isExpired && (
              <div className="bg-purple-100 px-3 py-1 rounded-full">
                <span className="text-purple-700 font-bold text-sm">+15 max</span>
              </div>
            )}
            <ChevronRight className="w-6 h-6 text-slate-400" />
          </div>
        </button>

        {/* Enter Coin World Banner */}
        <button
          onClick={() => navigate('/coin-world')}
          className="w-full card-bubbly bg-gradient-to-br from-amber-50 to-orange-100 text-left hover-bounce tap-shrink"
          data-testid="coin-world-btn"
        >
          <div className="flex items-center gap-4">
            <div className="text-5xl">🪙</div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-slate-900 font-fredoka">
                Enter Coin World!
              </h3>
              <p className="text-slate-600">
                Learn to save, invest & spend wisely with your coins!
              </p>
            </div>
            <ChevronRight className="w-6 h-6 text-slate-400" />
          </div>
        </button>

        {/* Real Prizes Banner */}
        <button
          onClick={() => navigate('/real-prizes')}
          className="w-full card-bubbly bg-gradient-to-br from-rose-100 via-amber-50 to-yellow-100 text-left hover-bounce tap-shrink border-2 border-amber-200"
          data-testid="real-prizes-btn"
        >
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center shadow-lg">
              <Trophy className="w-7 h-7 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-slate-900 font-fredoka">
                Real Prizes
              </h3>
              <p className="text-slate-600">
                Complete goals to earn real-world rewards!
              </p>
            </div>
            <ChevronRight className="w-6 h-6 text-slate-400" />
          </div>
        </button>

        {/* Skill Garden Banner */}
        <button
          onClick={() => navigate('/skill-garden')}
          className="w-full card-bubbly bg-gradient-to-br from-green-50 to-sky-50 text-left hover-bounce tap-shrink"
          data-testid="skill-garden-btn"
        >
          <div className="flex items-center gap-4">
            <div className="text-5xl">🌱</div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-slate-900 font-fredoka">
                My Skill Garden
              </h3>
              <p className="text-slate-600">
                Watch your brain skills grow like a garden!
              </p>
            </div>
            <ChevronRight className="w-6 h-6 text-slate-400" />
          </div>
        </button>

        {/* Cognitive Passport */}
        <button
          onClick={() => navigate('/cognitive-passport')}
          className="w-full card-bubbly bg-gradient-to-br from-amber-50 to-yellow-50 text-left hover-bounce tap-shrink"
          data-testid="cognitive-passport-btn"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
              <Award className="w-6 h-6 text-amber-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-slate-900 font-fredoka">
                Cognitive Passport
              </h3>
              <p className="text-sm text-slate-500">
                Download your progress certificate
              </p>
            </div>
            <ChevronRight className="w-6 h-6 text-slate-400" />
          </div>
        </button>

        {/* Extend Access / Payment Card */}
        <div className="card-bubbly bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200" data-testid="payment-card">
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <div className="w-14 h-14 bg-blue-100 rounded-2xl flex items-center justify-center">
              <span className="text-2xl font-black text-blue-600">$</span>
            </div>
            <div className="flex-1 text-center sm:text-left">
              <h3 className="text-lg font-bold text-slate-900 font-fredoka">
                {subStatus?.active ? 'Weekly Subscription Active' : isExpired ? 'Renew Your Access' : 'Extend Your Journey'}
              </h3>
              <p className="text-sm text-slate-500">
                {subStatus?.active
                  ? '$3.99/week for 12 weeks — cancel anytime'
                  : isExpired
                    ? 'Your access has expired. Subscribe to continue!'
                    : daysLeft !== null
                      ? `${daysLeft} day${daysLeft !== 1 ? 's' : ''} left — add more time anytime`
                      : '$3.99/week for 12 weeks — keep the brain training going!'}
              </p>
            </div>
            {subStatus?.active ? (
              <button
                onClick={handleCancelSubscription}
                disabled={cancelLoading}
                className="text-sm font-bold text-red-500 hover:text-red-700 border border-red-200 rounded-xl px-4 py-2 hover:bg-red-50 transition-all whitespace-nowrap"
                data-testid="cancel-sub-btn"
              >
                {cancelLoading ? 'Cancelling...' : 'Cancel Subscription'}
              </button>
            ) : (
              <button
                onClick={handleStripePayment}
                disabled={stripeLoading}
                className="btn-primary flex items-center gap-2 whitespace-nowrap"
                data-testid="pay-now-btn"
              >
                {stripeLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <span className="font-bold">Subscribe $3.99/wk</span>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Streak */}
          <div className="card-bubbly text-center hover-bounce" data-testid="streak-card">
            <div className="w-14 h-14 mx-auto bg-orange-100 rounded-full flex items-center justify-center mb-3">
              <Flame className="w-7 h-7 text-orange-500" />
            </div>
            <p className="text-3xl font-black text-slate-900">{user?.streak || 0}</p>
            <p className="text-sm font-medium text-slate-500">Day Streak</p>
          </div>

          {/* Math Level */}
          <div className="card-bubbly text-center hover-bounce" data-testid="math-level-card">
            <div className="w-14 h-14 mx-auto bg-blue-100 rounded-full flex items-center justify-center mb-3">
              <span className="text-2xl">🔢</span>
            </div>
            <p className="text-3xl font-black text-slate-900">Lv.{user?.math_level || 1}</p>
            <p className="text-sm font-medium text-slate-500">Math Level</p>
          </div>

          {/* Brain Power - replaces Chess Level */}
          <div className="card-bubbly text-center hover-bounce" data-testid="brain-power-card">
            <div className="w-14 h-14 mx-auto bg-purple-100 rounded-full flex items-center justify-center mb-3">
              <Brain className="w-7 h-7 text-purple-500" />
            </div>
            <p className="text-3xl font-black text-slate-900">{Math.min((user?.total_sessions || 0) * 10, 100)}%</p>
            <p className="text-sm font-medium text-slate-500">Brain Power</p>
          </div>

          {/* Total Sessions */}
          <div className="card-bubbly text-center hover-bounce" data-testid="sessions-card">
            <div className="w-14 h-14 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-3">
              <Star className="w-7 h-7 text-green-500" />
            </div>
            <p className="text-3xl font-black text-slate-900">{progress?.total_sessions || 0}</p>
            <p className="text-sm font-medium text-slate-500">Sessions Done</p>
          </div>
        </div>

        {/* Journey Progress */}
        <div className="card-bubbly" data-testid="journey-progress">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-xl font-bold text-slate-900 font-fredoka">12-Week Journey</h3>
              <p className="text-slate-500">Week {weekProgress} of 12</p>
            </div>
            <div className="text-4xl">
              {weekProgress <= 3 ? '🌱' : weekProgress <= 6 ? '🌿' : weekProgress <= 9 ? '🌳' : '🏆'}
            </div>
          </div>
          
          <Progress value={journeyProgress} className="h-4 rounded-full bg-slate-100" />
          
          <div className="flex justify-between mt-2 text-sm text-slate-500">
            <span>{progress?.total_sessions || 0} sessions completed</span>
            <span>{84 - (progress?.total_sessions || 0)} to go</span>
          </div>
        </div>

        {/* Quick Stats Row */}
        <div className="grid grid-cols-3 gap-4">
          <div className="card-bubbly text-center py-4">
            <p className="text-2xl font-black text-slate-900">{progress?.total_minutes || 0}</p>
            <p className="text-sm text-slate-500">Minutes Practiced</p>
          </div>
          <div className="card-bubbly text-center py-4">
            <p className="text-2xl font-black text-slate-900">{progress?.active_days || 0}</p>
            <p className="text-sm text-slate-500">Active Days</p>
          </div>
          <div className="card-bubbly text-center py-4">
            <p className="text-2xl font-black text-slate-900">{user?.coins || 0}</p>
            <p className="text-sm text-slate-500">Brain Coins</p>
          </div>
        </div>

        {/* Push Notifications */}
        <NotificationManager />

        {/* Mystery Box Surprise Popup */}
        {showMysteryBox && (
          <div data-testid="mystery-box-popup" className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in">
            {/* Sparkle particles */}
            {!boxOpened && [...Array(8)].map((_, i) => (
              <span key={i} className="absolute text-2xl animate-coin-rain" style={{
                left: `${15 + Math.random() * 70}%`, top: `${5 + Math.random() * 25}%`,
                animationDelay: `${Math.random() * 1.5}s`,
              }}>{['✨', '⭐', '💫', '🌟'][i % 4]}</span>
            ))}

            <div className="bg-white rounded-3xl shadow-2xl max-w-sm mx-4 p-6 text-center animate-pop relative">
              {!boxOpened ? (
                <>
                  <div className="text-4xl mb-2">✨🎁✨</div>
                  <h2 className="text-xl font-black text-slate-900 font-fredoka mb-1">Mystery Box!</h2>
                  <p className="text-slate-500 text-sm mb-5">A magical surprise appeared!</p>

                  <button data-testid="open-mystery-box-btn" onClick={handleOpenMysteryBox} disabled={boxOpening}
                    className={`w-full py-3 rounded-2xl font-bold text-base transition-all ${
                      boxOpening
                        ? 'bg-gradient-to-r from-purple-300 to-pink-300 text-white animate-pulse'
                        : 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg hover:shadow-xl hover:scale-105'
                    }`}
                  >
                    {boxOpening ? '🔮 Opening...' : '🎁 Open Mystery Box!'}
                  </button>

                  {!boxOpening && (
                    <button onClick={() => setShowMysteryBox(false)}
                      className="mt-3 text-xs text-slate-400 hover:text-slate-600 transition-colors">
                      Maybe later
                    </button>
                  )}
                </>
              ) : mysteryReward ? (
                <div className="animate-pop">
                  <div className="text-5xl mb-3">{mysteryReward.reward?.emoji || '🎁'}</div>
                  <h3 className="text-lg font-black text-slate-900 mb-1">{mysteryReward.reward?.name}!</h3>
                  <p className="text-slate-600 text-sm mb-1">{mysteryReward.reward?.desc}</p>
                  <span className={`inline-block mt-2 mb-4 px-3 py-1 rounded-full text-xs font-bold ${
                    mysteryReward.reward_type === 'title' ? 'bg-purple-100 text-purple-700' :
                    mysteryReward.reward_type === 'collection_item' ? 'bg-amber-100 text-amber-700' :
                    mysteryReward.reward_type === 'pet_food' ? 'bg-green-100 text-green-700' :
                    'bg-blue-100 text-blue-700'
                  }`}>
                    {mysteryReward.reward_type === 'pet_food' ? '🍎 Pet Food' : mysteryReward.reward_type === 'collection_item' ? '⭐ Rare Item' : mysteryReward.reward_type === 'title' ? '🏅 New Title' : '🪙 Coins'}
                  </span>
                  <button data-testid="mystery-box-done-btn" onClick={() => { setShowMysteryBox(false); setBoxOpened(false); setMysteryReward(null); }}
                    className="w-full py-3 bg-gradient-to-r from-green-400 to-emerald-500 text-white rounded-2xl font-bold text-sm hover:shadow-lg transition-all"
                  >
                    Awesome! 🎉
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        )}

        {/* Motivational Message */}
        <div className="text-center py-4">
          <p className="text-lg text-slate-600 italic">
            {user?.streak >= 7 
              ? "Wow! A whole week streak! You're amazing!"
              : user?.streak >= 3
              ? "Great streak going! Keep it up!"
              : "Every day counts! You're building brain power!"}
          </p>
        </div>
      </main>

      {/* Brain Boss Surprise Popup — Timer Pressure Edition */}
      {showBoss && bossQuestion && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100]" data-testid="boss-popup">
          <div className="max-w-sm w-full mx-4 animate-pop">
            <div className="bg-slate-900 rounded-3xl shadow-2xl border border-yellow-500/30 overflow-hidden">
              {/* Boss Header with Timer */}
              <div className="px-5 pt-5 pb-3 text-center relative">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-12 h-12 bg-yellow-500/20 rounded-xl flex items-center justify-center border border-yellow-500/40">
                    <Crown className="w-6 h-6 text-yellow-400" />
                  </div>
                  {/* Countdown Timer */}
                  {!bossResult && (
                    <div className={`w-14 h-14 rounded-full flex items-center justify-center border-4 transition-colors ${
                      bossTimer <= 2 ? 'border-red-500 bg-red-500/20 animate-pulse' : 
                      bossTimer <= 3 ? 'border-amber-400 bg-amber-500/10' : 
                      'border-yellow-400/50 bg-yellow-500/10'
                    }`} data-testid="boss-timer">
                      <span className={`text-xl font-black ${
                        bossTimer <= 2 ? 'text-red-400' : 'text-yellow-400'
                      }`}>{bossTimer}</span>
                    </div>
                  )}
                </div>
                <h2 className="text-lg font-black text-white">Brain Boss Challenge</h2>
                <p className="text-xs text-yellow-400/70 mt-0.5">Answer fast for <span className="font-bold text-yellow-400">+30 coins</span></p>
              </div>

              {/* Question */}
              <div className="px-5 py-3 bg-yellow-500/5 border-y border-yellow-500/10">
                <p className="text-white font-semibold text-sm leading-relaxed">{bossQuestion.question}</p>
              </div>

              <div className="px-5 py-4">
                {!bossResult ? (
                  <>
                    {/* 3 Options — A/B/C */}
                    <div className="space-y-2 mb-3">
                      {bossQuestion.options?.map((opt, idx) => (
                        <button key={idx} data-testid={`boss-option-${idx}`}
                          onClick={() => { setBossAnswer(opt); }}
                          disabled={bossLoading}
                          className={`w-full px-3.5 py-3 rounded-xl border-2 text-left font-semibold text-sm transition-all ${
                            bossAnswer === opt
                              ? 'border-yellow-400 bg-yellow-500/20 text-yellow-300'
                              : 'border-slate-700 bg-slate-800 hover:border-yellow-500/40 text-slate-300'
                          }`}
                        >
                          <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold mr-2.5 ${
                            bossAnswer === opt ? 'bg-yellow-400 text-slate-900' : 'bg-slate-700 text-slate-400'
                          }`}>{String.fromCharCode(65 + idx)}</span>
                          {opt}
                        </button>
                      ))}
                    </div>
                    <button data-testid="boss-submit-btn" onClick={handleBossSubmit} disabled={!bossAnswer || bossLoading}
                      className={`w-full py-3 rounded-xl font-bold text-sm transition-all ${
                        bossAnswer && !bossLoading
                          ? 'bg-yellow-400 hover:bg-yellow-300 text-slate-900 shadow-lg shadow-yellow-400/20'
                          : 'bg-slate-800 text-slate-600 cursor-not-allowed'
                      }`}
                    >
                      {bossLoading ? 'Checking...' : 'Lock In Answer'}
                    </button>
                    <button onClick={handleBossDismiss} className="w-full mt-2 py-1.5 text-xs text-slate-500 hover:text-slate-300" data-testid="boss-skip-btn">
                      Skip this time
                    </button>
                  </>
                ) : (
                  <div className="text-center py-2">
                    {bossResult.correct ? (
                      <div className="mb-4">
                        <div className="w-16 h-16 mx-auto mb-3 bg-emerald-500/20 rounded-2xl flex items-center justify-center border-2 border-emerald-400/50">
                          <Trophy className="w-8 h-8 text-emerald-400" />
                        </div>
                        <h3 className="text-xl font-black text-white">{bossWinMessages[0] || 'Boss Defeated!'}</h3>
                        <p className="text-yellow-400 font-bold text-sm mt-1">+30 Coins earned</p>
                      </div>
                    ) : (
                      <div className="mb-4">
                        <div className="w-16 h-16 mx-auto mb-3 bg-red-500/20 rounded-2xl flex items-center justify-center border-2 border-red-500/30">
                          <span className="text-3xl">{bossResult.timed_out ? '\u23F0' : '\uD83D\uDE35'}</span>
                        </div>
                        <h3 className="text-lg font-black text-white">
                          {bossResult.timed_out ? "Time's up!" : 'Not this time!'}
                        </h3>
                        <p className="text-amber-400/80 text-sm mt-1 font-medium italic">
                          {bossFailMessages[Math.floor(Math.random() * bossFailMessages.length)] || 'Better luck next time!'}
                        </p>
                        <p className="text-slate-400 text-xs mt-2">
                          Answer: <strong className="text-slate-300">{bossQuestion.correct_answer}</strong>
                        </p>
                      </div>
                    )}
                    <button data-testid="boss-continue-btn" onClick={handleBossDismiss}
                      className="w-full py-3 bg-yellow-400 hover:bg-yellow-300 text-slate-900 rounded-xl font-bold text-sm transition-all"
                    >
                      Continue
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Renewal Modal */}
      {showRenewModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100]" data-testid="renew-modal">
          <div className="bg-white rounded-3xl p-6 mx-4 max-w-sm w-full shadow-2xl animate-pop">
            <div className="text-center mb-5">
              <div className="w-14 h-14 mx-auto bg-primary/20 rounded-full flex items-center justify-center mb-3">
                <Ticket className="w-7 h-7 text-primary" />
              </div>
              <h3 className="text-xl font-black text-slate-900 font-fredoka">Renew Access</h3>
            </div>

            {renewTab === 'options' ? (
              <div className="space-y-3">
                {/* Free Trial */}
                {!trialUsed && (
                  <button
                    onClick={handleStartTrial}
                    disabled={trialLoading}
                    className="w-full p-4 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-2xl text-left hover:border-green-400 transition-all"
                    data-testid="start-trial-btn"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                        <Star className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="font-bold text-slate-900">7-Day Free Trial</p>
                        <p className="text-xs text-slate-500">Try free for a week, no payment needed</p>
                      </div>
                    </div>
                  </button>
                )}

                {/* Stripe Payment */}
                <button
                  onClick={handleStripePayment}
                  disabled={stripeLoading}
                  className="w-full p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-2xl text-left hover:border-blue-400 transition-all"
                  data-testid="stripe-pay-btn"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-lg font-bold text-blue-600">$</span>
                    </div>
                    <div>
                      <p className="font-bold text-slate-900">Subscribe — $3.99/week for 12 weeks</p>
                      <p className="text-xs text-slate-500">Billed weekly via Stripe. Cancel anytime.</p>
                    </div>
                    {stripeLoading && <Loader2 className="w-4 h-4 animate-spin ml-auto text-blue-500" />}
                  </div>
                </button>

                {/* Enrollment Code */}
                <button
                  onClick={() => setRenewTab('code')}
                  className="w-full p-4 bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200 rounded-2xl text-left hover:border-amber-400 transition-all"
                  data-testid="use-code-btn"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                      <Ticket className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-bold text-slate-900">Enrollment Code</p>
                      <p className="text-xs text-slate-500">Enter a code for 12 weeks access</p>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => { setShowRenewModal(false); setRenewTab('options'); }}
                  className="w-full py-2 text-sm text-slate-400 hover:text-slate-600"
                  data-testid="renew-cancel-btn"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <input
                  type="text"
                  value={renewCode}
                  onChange={(e) => setRenewCode(formatRenewCode(e.target.value))}
                  placeholder="BRAIN-XXXX-XXXX"
                  className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl font-mono text-lg tracking-wider uppercase text-center focus:border-primary focus:outline-none"
                  data-testid="renew-code-input"
                  maxLength={15}
                  disabled={renewLoading}
                />
                <button
                  onClick={handleRenew}
                  disabled={!renewCode.trim() || renewLoading}
                  className="w-full py-3 bg-primary hover:bg-primary/90 text-white rounded-xl font-bold transition-all disabled:opacity-50"
                  data-testid="renew-submit-btn"
                >
                  {renewLoading ? 'Renewing...' : 'Renew Access'}
                </button>
                <button
                  onClick={() => { setRenewTab('options'); setRenewCode(''); }}
                  className="w-full py-2 text-sm text-slate-400 hover:text-slate-600"
                >
                  Back to options
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
